import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { marketResearchAPI, planAPI } from '../lib/api';
import {
  BarChart3, Plus, Trash2, Save, Loader2, Wand2, ThumbsUp, ThumbsDown,
  Building2, CheckCircle2, FilePlus2,
} from 'lucide-react';

const FIELDS = [
  { key: 'hacen_bien', label: 'Qué hacen bien', icon: ThumbsUp },
  { key: 'hacen_mal', label: 'Qué hacen mal / puntos débiles', icon: ThumbsDown },
  { key: 'tipo_contenido', label: 'Tipo de contenido' },
  { key: 'observaciones', label: 'Observaciones' },
  { key: 'precios', label: 'Precios' },
];
const uid = () => Math.random().toString(36).slice(2, 10);
const emptyComp = () => ({ id: uid(), nombre: '', hacen_bien: '', hacen_mal: '', tipo_contenido: '', observaciones: '', precios: '' });

export default function MarketResearchSection({ clientId }) {
  const [competidores, setCompetidores] = useState([]);
  const [notas, setNotas] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [proposal, setProposal] = useState(null); // [{comp fields, _matchId, _include, _sel:Set}]
  const [proposalOpen, setProposalOpen] = useState(false);
  const [sourceFile, setSourceFile] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await marketResearchAPI.get(clientId);
      setCompetidores(res.data?.competidores || []);
      setNotas(res.data?.notas || '');
      setDirty(false);
    } catch { toast.error('Error cargando la investigación de mercado'); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const mutate = (next) => { setCompetidores(next); setDirty(true); };
  const setComp = (id, patch) => mutate(competidores.map((c) => c.id === id ? { ...c, ...patch } : c));
  const addComp = () => mutate([...competidores, emptyComp()]);
  const delComp = (id) => mutate(competidores.filter((c) => c.id !== id));

  const persist = async (comps, nts) => {
    setSaving(true);
    try {
      const res = await marketResearchAPI.save(clientId, { competidores: comps ?? competidores, notas: nts ?? notas });
      setCompetidores(res.data.competidores || []);
      setNotas(res.data.notas || '');
      setDirty(false);
      return true;
    } catch (e) { toast.error(e.message || 'No se pudo guardar'); return false; }
    finally { setSaving(false); }
  };
  const handleSave = async () => { if (await persist()) toast.success('Investigación de mercado guardada'); };

  const handleExtract = async () => {
    setExtracting(true);
    try {
      const res = await planAPI.extract(clientId, 'competencia');
      const list = (res.proposal || []).map((p) => {
        const match = competidores.find((c) => (c.nombre || '').trim().toLowerCase() === (p.nombre || '').trim().toLowerCase());
        const sel = new Set(FIELDS.filter((f) => (p[f.key] || '').trim()).map((f) => f.key));
        return { ...p, _matchId: match ? match.id : null, _include: true, _sel: sel };
      });
      setProposal(list);
      setSourceFile(res.source_file || '');
      setProposalOpen(true);
    } catch (e) { toast.error(e.message || 'No se pudo extraer del plan'); }
    finally { setExtracting(false); }
  };

  const toggleInclude = (i) => setProposal((p) => p.map((x, idx) => idx === i ? { ...x, _include: !x._include } : x));
  const toggleField = (i, key) => setProposal((p) => p.map((x, idx) => {
    if (idx !== i) return x;
    const s = new Set(x._sel); s.has(key) ? s.delete(key) : s.add(key); return { ...x, _sel: s };
  }));

  const applyProposal = async () => {
    let next = [...competidores];
    let added = 0, updated = 0;
    proposal.filter((p) => p._include).forEach((p) => {
      if (p._matchId) {
        next = next.map((c) => {
          if (c.id !== p._matchId) return c;
          const merged = { ...c };
          p._sel.forEach((k) => { if (p[k]) merged[k] = p[k]; });
          return merged;
        });
        updated += 1;
      } else {
        const nc = emptyComp();
        nc.nombre = p.nombre || '';
        p._sel.forEach((k) => { if (p[k]) nc[k] = p[k]; });
        next.push(nc); added += 1;
      }
    });
    setProposalOpen(false);
    await persist(next, notas);
    toast.success(`Aplicado: ${added} nuevo(s), ${updated} completado(s)`);
  };

  if (loading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;

  return (
    <div className="space-y-5" data-testid="market-research-section">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" /> Investigación de Mercado</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Análisis de la competencia: qué hacen bien y qué hacen mal.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button variant="outline" size="sm" onClick={handleExtract} disabled={extracting} data-testid="market-extract-btn">
                {extracting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} Extraer del plan
              </Button>
              <Button variant="outline" size="sm" onClick={addComp} data-testid="market-add-comp"><Plus className="w-4 h-4 mr-1" /> Añadir competidor</Button>
              <Button size="sm" onClick={handleSave} disabled={saving || !dirty} data-testid="market-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} {dirty ? 'Guardar cambios' : 'Guardado'}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {competidores.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="market-empty">
          Aún no hay competidores. Añade uno manualmente o pulsa "Extraer del plan" para sacarlos del PDF del Plan de Social Media.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {competidores.map((c) => (
            <Card key={c.id} data-testid={`market-comp-${c.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-primary shrink-0" />
                  <Input value={c.nombre} onChange={(e) => setComp(c.id, { nombre: e.target.value })} onBlur={() => dirty && persist()} placeholder="Nombre del competidor" className="h-8 font-medium" data-testid={`market-comp-nombre-${c.id}`} />
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => delComp(c.id)} data-testid={`market-del-comp-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs flex items-center gap-1">{f.icon && <f.icon className="w-3 h-3" />} {f.label}</Label>
                    <Textarea rows={2} value={c[f.key] || ''} onChange={(e) => setComp(c.id, { [f.key]: e.target.value })} onBlur={() => dirty && persist()} placeholder={f.key === 'precios' ? 'Solo si se conoce' : ''} data-testid={`market-comp-${f.key}-${c.id}`} />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Notas de mercado</CardTitle></CardHeader>
        <CardContent>
          <Textarea rows={4} value={notas} onChange={(e) => { setNotas(e.target.value); setDirty(true); }} onBlur={() => dirty && persist()} placeholder="Tendencias, oportunidades, posicionamiento…" data-testid="market-notas" />
        </CardContent>
      </Card>

      {/* Proposal dialog */}
      <Dialog open={proposalOpen} onOpenChange={setProposalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="market-proposal-dialog">
          <DialogHeader>
            <DialogTitle>Propuesta de competencia del plan</DialogTitle>
            <DialogDescription>
              Revisa y elige campo por campo qué aplicar. Nada se guarda hasta que pulses "Aplicar".{sourceFile ? ` Fuente: ${sourceFile}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {(!proposal || proposal.length === 0) && <p className="text-sm text-muted-foreground">La IA no propuso competidores.</p>}
            {proposal && proposal.map((p, i) => (
              <div key={i} className={`rounded-lg border p-3 space-y-2 ${p._include ? '' : 'opacity-50'}`} data-testid={`market-proposal-${i}`}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="accent-primary" checked={p._include} onChange={() => toggleInclude(i)} data-testid={`market-proposal-include-${i}`} />
                  <span className="font-medium">{p.nombre || 'Competidor'}</span>
                  {p._matchId ? (
                    <Badge variant="secondary" className="gap-1"><CheckCircle2 className="w-3 h-3" /> Completar ficha existente</Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1"><FilePlus2 className="w-3 h-3" /> Nueva ficha</Badge>
                  )}
                </label>
                {p._include && (
                  <div className="space-y-2 pl-6">
                    {FIELDS.filter((f) => (p[f.key] || '').trim()).map((f) => (
                      <label key={f.key} className="flex items-start gap-2 rounded-md border p-2 cursor-pointer hover:border-primary/40" data-testid={`market-proposal-field-${i}-${f.key}`}>
                        <input type="checkbox" className="accent-primary mt-1" checked={p._sel.has(f.key)} onChange={() => toggleField(i, f.key)} data-testid={`market-proposal-check-${i}-${f.key}`} />
                        <div>
                          <p className="text-xs font-medium text-muted-foreground">{f.label}</p>
                          <p className="text-sm text-emerald-700">{p[f.key]}</p>
                        </div>
                      </label>
                    ))}
                    {FIELDS.filter((f) => (p[f.key] || '').trim()).length === 0 && (
                      <p className="text-xs text-muted-foreground italic">Sin campos con datos para este competidor.</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProposalOpen(false)}>Cancelar</Button>
            <Button onClick={applyProposal} disabled={!proposal || !proposal.some((p) => p._include)} data-testid="market-proposal-apply">Aplicar seleccionados</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
